/**
 * Meta Ads Configuration API
 * Suporta múltiplos perfis Meta Ads
 * Usa RPC functions + queries diretas para acessar o schema grupod12
 * @updated 2026-04-15
 */

import { supabase } from '@/lib/supabase'
import { schemaQuery } from '@/lib/api/schema-resolver'
import type { MetaAdsConfig, MetaAdsConfigInput } from '@/types/meta-ads-config'

/**
 * Busca TODAS as configurações do Meta Ads (múltiplos perfis)
 */
export async function getAllMetaAdsConfigs(): Promise<MetaAdsConfig[]> {
  const { data, error } = await schemaQuery
    .table('meta_ads_config')
    .select('*')
    .order('created_at', { ascending: true })

  if (error) {
    console.error('[getAllMetaAdsConfigs] Erro:', error)
    throw error
  }

  return (data ?? []) as MetaAdsConfig[]
}

/**
 * Busca a configuração mais recente do Meta Ads (compatibilidade)
 */
export async function getMetaAdsConfig(): Promise<MetaAdsConfig | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_meta_ads_config')
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('[getMetaAdsConfig] Erro:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return data as MetaAdsConfig;
  } catch (error) {
    console.error('[getMetaAdsConfig] Falha:', error);
    throw error;
  }
}

/**
 * Salva configuração (cria ou atualiza via RPC)
 */
export async function saveMetaAdsConfig(
  config: MetaAdsConfigInput,
  userId: string
): Promise<MetaAdsConfig> {
  const { data, error } = await supabase
    .rpc('upsert_meta_ads_config', {
      p_perfil_meta: config.perfil_meta,
      p_url_contas: config.url_contas,
      p_token: config.token,
      p_token_data_validade: config.token_data_validade.toISOString(),
      p_date_preset: config.date_preset,
      p_api_version: config.api_version,
      p_insights_level: config.insights_level,
      p_insights_limit: config.insights_limit,
      p_time_increment: config.time_increment,
      p_user_id: userId,
    })
    .single();

  if (error) {
    console.error('[saveMetaAdsConfig] Erro:', error);
    throw error;
  }

  return data as MetaAdsConfig;
}

/**
 * Ativa ou desativa um perfil Meta Ads
 */
export async function toggleMetaAdsConfigActive(
  id: string,
  ativo: boolean
): Promise<void> {
  const { error } = await (schemaQuery
    .table('meta_ads_config')
    .update({ ativo, updated_at: new Date().toISOString() } as Record<string, unknown>) as ReturnType<typeof schemaQuery.table>)
    .eq('id', id as never)

  if (error) {
    console.error('[toggleMetaAdsConfigActive] Erro:', error)
    throw error
  }
}

/**
 * Deleta uma configuração do Meta Ads
 */
export async function deleteMetaAdsConfig(id: string): Promise<void> {
  const { error } = await (schemaQuery
    .table('meta_ads_config')
    .delete() as ReturnType<typeof schemaQuery.table>)
    .eq('id', id as never)

  if (error) {
    console.error('[deleteMetaAdsConfig] Erro:', error)
    throw error
  }
}

/**
 * Verifica o status do token (via RPC function)
 */
export async function getTokenStatusAPI(): Promise<{
  config_id: string;
  status: string;
  days_until_expiration: number;
  token_validade: string;
} | null> {
  const { data, error } = await supabase
    .rpc('check_meta_ads_token_status')
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('[getTokenStatus] Erro:', error);
    throw error;
  }

  return data as { config_id: string; status: string; days_until_expiration: number; token_validade: string } | null;
}

/**
 * Valida se o token do Meta Ads está funcionando
 */
export async function validateMetaAdsToken(token: string): Promise<boolean> {
  return token.length > 10;
}
